<template>
  <dialog ref="dialog" class="sp-dialog" part="base" :class="classModifiers" :aria-label="label">
    <div ref="content" class="sp-dialog__content" tabindex="-1">
      <slot />
    </div>
  </dialog>
</template>

<script setup>
import { computed, useTemplateRef } from "vue";
import { toBoolean } from "#ce/utils/props";
import { defaultWidth, drawerPositions, props as dialogProps } from "./props.js";
import { useDialog } from "./composables/useDialog.js";

defineOptions({
  inheritAttrs: false,
});

const emit = defineEmits(["close", "update-open"]);

const props = defineProps(dialogProps);

const dialog = useTemplateRef("dialog");
const content = useTemplateRef("content");

const { state } = useDialog(props, emit, { dialog, content });

// A drawer whose `width` is left at the default takes the deployment-themeable
// drawer token; any other `width` wins.
const resolvedWidth = computed(() =>
  drawerPositions.includes(props.position) && props.width === defaultWidth
    ? "var(--sp-comp-dialog-drawer-width, 26rem)"
    : props.width,
);

const classModifiers = computed(() => ({
  "--no-backdrop": toBoolean(props.noBackdrop),
  "--persistent": toBoolean(props.persistent),
  "--fullscreen": toBoolean(props.fullscreen),
  "--no-scrollable": toBoolean(props.noScrollable),
  [`--state-${state.value}`]: state.value,
  [`--position-${props.position}`]: props.position,
}));
</script>

<style lang="scss">
:host {
  display: block;
  --sp-comp-dialog-margin: auto;
  --sp-comp-dialog-width: auto;
  --sp-comp-dialog-max-inline-size: 60ch;
}

dialog {
  $backdrop-opacity: var(
    --sp-comp-dialog-backdrop-opacity,
    0.3
  ); // Safari (>=17.1) doesn't support --custom-property in ::backdrop
  --ease: ease-in-out;
  --animation-time: v-bind(animationTime);

  --animation-on-open: open--fade-in var(--animation-time) var(--ease);
  --animation-on-close: close--fade-out var(--animation-time) var(--ease);

  display: none;
  z-index: var(--sp-sys-z-index-max, 99);
  max-inline-size: min(90vw, var(--sp-comp-dialog-max-inline-size));

  position: fixed;
  width: v-bind(resolvedWidth);
  min-width: v-bind(resolvedWidth);
  height: var(--dialog-height, v-bind(height));
  min-height: var(--dialog-min-height, v-bind(height));
  // The body scrolls by default, so cap the height at 90vh when no explicit
  // max-height is set. --no-scrollable reverts this to size-to-content.
  max-height: var(--dialog-max-height, 90vh);
  overflow: hidden;
  opacity: 1;

  border-radius: var(--sp-comp-dialog-border-radius, 0.5rem);
  padding: var(--sp-comp-dialog-padding, 0);
  margin: var(--sp-comp-dialog-margin, auto);
  border: var(--sp-comp-dialog-border, 0);

  animation-timing-function: var(--ease);
  // transition: all 0.3s var(--ease);

  &::backdrop {
    background-color: var(--sp-comp-dialog-backdrop-background-color, #000);
    opacity: 0;
  }

  &.--state-closing,
  &.--state-opening,
  &.--state-open {
    display: block;
  }

  &.--state-opening,
  &.--state-closing {
    &::backdrop {
      animation: backdrop-fade var(--animation-time, 0.2s) ease-in-out;
    }
  }

  &.--state-open {
    &::backdrop {
      opacity: $backdrop-opacity;
    }
  }

  &.--state-opening {
    animation: var(--animation-on-open) forwards;

    &::backdrop {
      animation-direction: forwards;
    }
  }

  &.--state-closing {
    animation: var(--animation-on-close) forwards;

    &::backdrop {
      animation-direction: reverse;
    }
  }

  &.--state-closed {
    display: none;
  }

  &.--fullscreen {
    max-inline-size: 100vw;
    max-block-size: 100vh;
  }

  &.--no-backdrop::backdrop {
    background-color: transparent;
  }

  &.--position-bottom {
    --animation-on-close: close--slide-out-down var(--animation-time) var(--ease);
    --animation-on-open: open--slide-in-up var(--animation-time) var(--ease);
    inset: auto 0 0 0;
    // The software keyboard covers the layout viewport's bottom edge without
    // shrinking it, so a wrapper (SpBottomSheet) lifts the dialog by the
    // keyboard's height via --dialog-keyboard-translate. The independent
    // `translate` property composes with the keyframes' `transform`, and the
    // transition smooths the lift — iOS reports the keyboard in a single late
    // resize. The fallback must be `none`, never a zero length: any non-none
    // translate makes the dialog a containing block for fixed-position
    // descendants (sp-tooltip, sp-popup's non-top-layer fallback) and `none`
    // still interpolates as the identity, so the lift animates either way.
    translate: var(--dialog-keyboard-translate, none);
    transition: translate var(--animation-time) var(--ease);
    max-inline-size: 100vw;
  }

  // Drawers: docked to a vertical edge over the full viewport height, flush to
  // it (no margin, no radius) and free of the centred dialog's inline cap. The
  // width comes from the `width` prop, which falls back to the drawer token —
  // see `resolvedWidth`.
  &.--position-left,
  &.--position-right {
    margin: var(--sp-comp-dialog-drawer-margin, 0);
    max-inline-size: var(--sp-comp-dialog-drawer-max-inline-size, 100vw);
    height: var(--dialog-height, 100dvh);
    min-height: var(--dialog-min-height, 100dvh);
    max-height: var(--dialog-max-height, 100dvh);
    border-radius: var(--sp-comp-dialog-drawer-border-radius, 0);

    // Pin the wrapper to the drawer's height so a percentage-sized card
    // resolves against it and the card's own content region scrolls.
    .sp-dialog__content {
      height: var(--sp-comp-dialog-content-height, 100%);
    }
  }

  &.--position-left {
    --animation-on-close: close--slide-out-left var(--animation-time) var(--ease);
    --animation-on-open: open--slide-in-left var(--animation-time) var(--ease);
    inset: 0 auto 0 0;
  }

  &.--position-right {
    --animation-on-close: close--slide-out-right var(--animation-time) var(--ease);
    --animation-on-open: open--slide-in-right var(--animation-time) var(--ease);
    inset: 0 0 0 auto;
  }

  .sp-dialog__content {
    height: var(--sp-comp-dialog-content-height, auto);
    // Programmatic focus target (focus moves here on open). The dialog is the
    // visual boundary, so the wrapper carries no focus ring.
    outline: none;
    // The body scrolls by default; max-height: inherit bounds it to the dialog.
    overflow-y: auto;
    max-height: inherit;
  }

  &.--no-scrollable {
    max-height: var(--dialog-max-height, v-bind(height));

    .sp-dialog__content {
      overflow-y: visible;
      max-height: none;
    }
  }

  &:not([open]) {
    pointer-events: none;
  }

  @keyframes open--slide-in-up {
    0% {
      transform: translateY(100%);
    }
  }

  @keyframes close--slide-out-down {
    100% {
      transform: translateY(100%);
    }
  }

  @keyframes open--slide-in-right {
    0% {
      transform: translateX(100%);
    }
  }

  @keyframes close--slide-out-right {
    100% {
      transform: translateX(100%);
    }
  }

  @keyframes open--slide-in-left {
    0% {
      transform: translateX(-100%);
    }
  }

  @keyframes close--slide-out-left {
    100% {
      transform: translateX(-100%);
    }
  }

  @keyframes open--fade-in {
    0% {
      opacity: 0;
    }

    100% {
      opacity: 1;
    }
  }

  @keyframes close--fade-out {
    0% {
      opacity: 1;
    }

    100% {
      opacity: 0;
    }
  }

  @keyframes backdrop-fade {
    0% {
      opacity: 0;
    }

    100% {
      opacity: $backdrop-opacity;
    }
  }
}
</style>
